import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useWorkflow } from "@/hooks/useWorkflow";
import { Loader2 } from "lucide-react";

export const TopicInput: React.FC = () => {
    const [customTopic, setCustomTopic] = useState<string>('');
    const [isStarting, setIsStarting] = useState<boolean>(false);
    const { workflowState, startWorkflow } = useWorkflow();

    const handleGenerateIdea = async (): Promise<void> => {
        setIsStarting(true);
        try {
            await startWorkflow();
        } finally {
            // Reset after a short delay to show immediate feedback
            setTimeout(() => setIsStarting(false), 1000);
        }
    };

    const handleCustomTopic = async (): Promise<void> => {
        if (customTopic.trim()) {
            setIsStarting(true);
            try {
                await startWorkflow(customTopic.trim());
                setCustomTopic('');
            } finally {
                // Reset after a short delay to show immediate feedback
                setTimeout(() => setIsStarting(false), 1000);
            }
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter' && !workflowState.isActive && !isStarting) {
            handleCustomTopic();
        }
    };

    const isDisabled = workflowState.isActive && workflowState.currentStage !== 'final';
    const showLoading = isStarting || (workflowState.isActive && workflowState.currentStage !== 'final');

    const getButtonText = () => {
        if (isStarting) return "Getting ready...";
        if (workflowState.isActive && workflowState.currentStage !== 'final') return "Creating content...";
        return "Generate Topic for Me";
    };

    const getCustomButtonText = () => {
        if (isStarting) return "Getting ready...";
        if (workflowState.isActive && workflowState.currentStage !== 'final') return "Creating content...";
        return "Use My Topic";
    };

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>Choose Your Content Approach</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div>
                    <Button
                        onClick={handleGenerateIdea}
                        className="w-full touch-manipulation select-none"
                        style={{ touchAction: 'manipulation' }}
                        size="lg"
                        disabled={isDisabled || isStarting}
                    >
                        {showLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {getButtonText()}
                    </Button>
                    <p className="text-sm text-gray-600 mt-2">
                        Let AI create a thoughtful topic based on your style and voice
                    </p>
                </div>

                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">Or</span>
                    </div>
                </div>

                <div className="space-y-3">
                    <Input
                        placeholder="Enter your own topic or idea..."
                        value={customTopic}
                        onChange={(e) => setCustomTopic(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isDisabled || isStarting}
                        className="touch-manipulation"
                        style={{ touchAction: 'manipulation' }}
                    />
                    <Button
                        onClick={handleCustomTopic}
                        variant="outline"
                        className="w-full touch-manipulation select-none"
                        style={{ touchAction: 'manipulation' }}
                        disabled={!customTopic.trim() || isDisabled || isStarting}
                    >
                        {showLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {getCustomButtonText()}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};